import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import { usePeriod } from '@/lib/period';
import { PeriodControl } from '@/features/shared/PeriodControl';
import { CancellationsChart } from './CancellationsChart';
import { CancelledTenantsTable } from './CancelledTenantsTable';
import { ChurnRateChart } from './ChurnRateChart';
import { CreatedTenantsTable } from './CreatedTenantsTable';
import { NewSubscriptionsChart } from './NewSubscriptionsChart';
import { RevenueByCountryChart } from './RevenueByCountryChart';
import { RevenueByPlanChart } from './RevenueByPlanChart';
import { useSalesMetrics } from './useSalesMetrics';

/**
 * Tela `/dashboard/sales`: receita direta por país e plano, novas
 * assinaturas, cancelamentos e churn diário no período, e as tabelas de
 * empresas criadas e canceladas.
 *
 * Origem: sem issue — commit 8e8dea1c0, 2026-05-04 (rota /dashboard/sales).
 *
 * Contrato: sem props; período pelo `usePeriod`. Estados LOADING/ERROR/READY
 * vêm de `useSalesMetrics`; a tela só distribui `state.metrics` pelos
 * gráficos e tabelas.
 *
 * Usado por: `routes/router.tsx` (rota `dashboard/sales`, só super-admin).
 *
 * Arquitetura: só apresentação, sem escrita; toda leitura e a régua de
 * receita (`subscriptionRevenue.ts`) estão no `useSalesMetrics`. Spec:
 * `docs/screens/dashboard-sales.md`.
 *
 * Issues: #795 (ADM-4 — receita lida de `billing/subscription`).
 */
export function SalesDashboardPage(): React.JSX.Element {
  const { t } = useTranslation();
  const { period, setPeriod } = usePeriod();
  const state = useSalesMetrics(period);

  return (
    <ContentLayout
      header={<Header variant="h1" description={t('sales.subtitle')}>{t('sales.title')}</Header>}
    >
      <SpaceBetween size="l">
        <PeriodControl value={period} onChange={setPeriod} />

        {state.kind === 'LOADING' && (
          <Box textAlign="center" padding="l">
            <Spinner size="large" />
          </Box>
        )}
        {state.kind === 'ERROR' && <Alert type="error">{state.error}</Alert>}
        {state.kind === 'READY' && (
          <SpaceBetween size="l">
            <ColumnLayout columns={2}>
              <RevenueByCountryChart byCountryUsdCents={state.metrics.revenueByCountryUsdCents} />
              <RevenueByPlanChart byPlanUsdCents={state.metrics.revenueByPlanUsdCents} />
              <NewSubscriptionsChart series={state.metrics.newSubscriptionsByDay} />
              <CancellationsChart series={state.metrics.cancellationsByDay} />
              <ChurnRateChart series={state.metrics.churnRateByDay} />
            </ColumnLayout>
            <CreatedTenantsTable rows={state.metrics.createdTenants} />
            <CancelledTenantsTable rows={state.metrics.cancelledTenants} />
          </SpaceBetween>
        )}
      </SpaceBetween>
    </ContentLayout>
  );
}
